import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useVendor } from './CapabilitiesContext';
import { vendorApiClient, type VendorProfile } from './vendorApiClient';
import type { ProductCounts } from './setup';
import { computeSetup, type SetupProgress } from './setup';

/**
 * Shares one profile + product-count fetch across the member area so the SetupBanner,
 * the onboarding gate, and the wizard all agree on setup progress without each
 * re-fetching. Onboarding state (Vendor.status) drives the §2 Setup/pending
 * screen; data-derived progress drives the §9 banner + wizard.
 */
interface SetupState {
  loading: boolean;
  profile: VendorProfile | null;
  /** How many products the vendor has, and how many shoppers can see at their first location. */
  products: ProductCounts | null;
  status: string | null;
  progress: SetupProgress;
  refresh: () => void;
}

const SetupContext = createContext<SetupState | null>(null);

export function SetupProvider({ children }: { children: ReactNode }) {
  const { activeVendorId } = useVendor();
  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [products, setProducts] = useState<ProductCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!activeVendorId) return;
    let alive = true;
    setLoading(true);
    void Promise.allSettled([
      vendorApiClient.getProfile(activeVendorId),
      vendorApiClient.listProducts(activeVendorId, { pageSize: 10 }),
    ]).then(([p, c]) => {
      if (!alive) return;
      if (p.status === 'fulfilled') setProfile(p.value);
      if (c.status === 'fulfilled') setProducts({ total: c.value.counts.all, live: c.value.counts.live });
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [activeVendorId, nonce]);

  const value: SetupState = {
    loading,
    profile,
    products,
    status: profile?.status ?? null,
    progress: computeSetup(profile, products),
    refresh: () => setNonce((n) => n + 1),
  };

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}

export function useSetup(): SetupState {
  const ctx = useContext(SetupContext);
  if (!ctx) throw new Error('useSetup must be used within a SetupProvider');
  return ctx;
}
